import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { QueryParams } from '@/api/requests/monitoring/interfaces';
import {
  Stack,
  SwitchLabel,
  Text,
  useTheme,
  useMediaQuery,
} from '@/theme/index';
import { capitalizeString, formatPhone } from '@/utils/index';
import {
  ContactReportSearchData,
  UnclaimedMoneySearchData,
  MonitorItem,
  ReportMonitor,
  isPersonReportSearchData,
  isPhoneReportSearchData,
  isPropertyReportSearchData,
  isSocialNetworkReportSearchData,
  isUsernameReportSearchData,
  isVehicleReportSearchData,
  reportTypes,
} from './Components';
import { useReportMonitors } from '../useReportMonitors';

/**
 * This component shows the info for an individual report monitor in the list of active monitors. It has a toggle to turn the monitor on or off.
 */
export function ActiveMonitor({
  monitor,
  hasBeenDeleted,
  setHasBeenDeleted,
}: {
  monitor: ReportMonitor;
  hasBeenDeleted: { [key: string]: boolean };
  setHasBeenDeleted: Dispatch<SetStateAction<{ [key: string]: boolean }>>;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [title, setTitle] = useState<string | null>(null);
  const [reportSubtitle, setReportSubtitle] = useState<string | null>(null);

  const { id, permalink, report_type, search_data } = monitor;

  const { toggleReportIsMonitored } = useReportMonitors(permalink);

  const queryParams: QueryParams = {
    'report_monitor[permalink]': permalink,
    'report_monitor[is_claimed]': 'false',
    'report_monitor[search_data]': '',
    'report_monitor[report_type]': report_type || '',
  };

  function handleToggleReportMonitor(activeState: boolean) {
    setHasBeenDeleted({ ...hasBeenDeleted, [permalink]: !activeState });
    toggleReportIsMonitored({ queryParams });
  }

  function updateName() {
    if (search_data) {
      const data = search_data;
      if (isPersonReportSearchData(data)) {
        const name = `${capitalizeString(data.first_name)} ${capitalizeString(
          data.last_name
        )}`;
        setTitle(name);
        return;
      }
      if (report_type === 'unclaimed_money_report') {
        const unclaimedMoneyData = data as UnclaimedMoneySearchData;
        const name = `${capitalizeString(
          unclaimedMoneyData.first_name
        )} ${capitalizeString(unclaimedMoneyData.last_name)}`;
        setTitle(name);
        return;
      }
      if (report_type === 'contact_report') {
        const contactData = data as ContactReportSearchData;

        setTitle(`${contactData.first_name} ${contactData.last_name}`);

        return;
      }
      if (isPhoneReportSearchData(data)) {
        setTitle(formatPhone(data.phone_number));
        return;
      }
      if (isPropertyReportSearchData(data)) {
        setTitle(data.address);
        return;
      }
      if (isSocialNetworkReportSearchData(data)) {
        setTitle(data.email);
        return;
      }
      if (isVehicleReportSearchData(data)) {
        if (data.vin) {
          setTitle(data.vin);
        } else if (data.license_plate) {
          setTitle(data.license_plate);
        } else if (data.make && data.model) {
          setTitle(`${data.year} ${data.make} ${data.model}`);
        }
      }
    }
  }

  function updateReportSubtitle() {
    const data = search_data;
    const locationAgnosticReportType =
      !data ||
      isSocialNetworkReportSearchData(data) ||
      isVehicleReportSearchData(data) ||
      isUsernameReportSearchData(data) ||
      report_type === 'contact_report';

    if (locationAgnosticReportType) {
      return;
    }
    if (data.state) {
      if (data?.city) {
        setReportSubtitle(`${data.city}, ${data.state}`);
        return;
      }
      setReportSubtitle(data.state);
    }
  }

  useEffect(() => {
    updateName();
    updateReportSubtitle();
  }, [monitor]);

  return (
    <MonitorItem
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      key={id}
    >
      <Stack
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'flex-start',
          alignItems: isMobile ? 'flex-start' : 'row',
          overflow: isMobile ? 'hidden' : 'visible',
        }}
      >
        {reportTypes[report_type][1]}
        <Stack width={isMobile ? '100%' : 'auto'}>
          <Text
            color="text.secondary"
            variant="body2"
            sx={{
              whiteSpace: isMobile ? 'nowrap' : 'normal',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {reportTypes[report_type][0]}
          </Text>
          <Text
            sx={{
              fontWeight: 'bold',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            data-testid={`monitor-title-${permalink}`}
          >
            {title}
          </Text>
          <Text color="text.secondary">{reportSubtitle}</Text>
        </Stack>
      </Stack>

      <SwitchLabel
        label=""
        checked={!hasBeenDeleted[permalink]}
        setChecked={(e) => handleToggleReportMonitor(e)}
      />
    </MonitorItem>
  );
}
